import React, { useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { styled } from "@mui/material/styles";
import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import MuiAccordion from "@mui/material/Accordion";
import MuiAccordionSummary, {
  accordionSummaryClasses,
} from "@mui/material/AccordionSummary";
import MuiAccordionDetails from "@mui/material/AccordionDetails";
import useFetchWithAuth from "../../../features/hooks/useFetchWithAuth";
import { canAccess, getRules } from "../../../util/rbac";

// Styled components
const Accordion = styled(MuiAccordion)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));

// Main component
const LeftSidebarMenu = ({ toggleActive, menuSections = [], logo }) => {
  const [expanded, setExpanded] = useState(false);
  const location = useLocation();
  const { data: meData } = useFetchWithAuth("/user/me/");
  const rules = useMemo(() => getRules(meData), [meData]);

  const isActiveLink = (path) => location.pathname === path;

  const handleChange = (panelId) => (event, newExpanded) => {
    setExpanded(newExpanded ? panelId : false);
  };

  const getRolePrefix = (value) => {
    const raw = String(value || "").toLowerCase();
    if (raw.includes("supervisor")) return "supervisor";
    if (raw.includes("operator")) return "operator";
    if (raw.includes("management") || raw.includes("manager")) return "management";
    return "";
  };

  const rolePrefix = useMemo(
    () => getRolePrefix(meData?.access_role?.name || meData?.role?.name || meData?.role),
    [meData]
  );

  const getRoleBadge = (resource) => {
    if (!resource) return null;
    if (resource.startsWith("operator.")) return "O";
    if (resource.startsWith("supervisor.")) return "S";
    if (resource.startsWith("management.")) return "M";
    return null;
  };

  const filteredSections = useMemo(() => {
    const RESOURCE_ITEMS = [
      { resource: "operator.dashboard", label: "Дашборд", path: "/operator/dashboard", icon: "dashboard" },
      { resource: "operator.scripts", label: "Скрипти", path: "/operator/scripts/", icon: "contact_page" },
      { resource: "operator.lms", label: "LMS / Навчання", path: "/operator/lms/", icon: "auto_stories" },
      { resource: "operator.rules", label: "Правила роботи", path: "/operator/rules/", icon: "group_work" },
      { resource: "operator.work_schedule", label: "Графік роботи", path: "/operator/work-schedule", icon: "calendar_month" },
      { resource: "supervisor.dashboard", label: "Дашборд", path: "/supervisor/dashboard/", icon: "dashboard" },
      { resource: "supervisor.scripts", label: "Скрипти", path: "/supervisor/scripts/", icon: "contact_page" },
      { resource: "supervisor.operators", label: "Оператори", path: "/supervisor/operators/", icon: "person_outline" },
      { resource: "supervisor.work_schedule", label: "Графік роботи", path: "/supervisor/work-schedule/", icon: "date_range" },
      { resource: "management.dashboard", label: "Дашборд", path: "/management/dashboard", icon: "dashboard" },
      { resource: "management.operators", label: "Оператори", path: "/management/operators/", icon: "person_outline" },
      { resource: "management.scripts", label: "Скрипти", path: "/management/scripts/", icon: "contact_page" },
      { resource: "management.work_schedule", label: "Графік роботи", path: "/management/work-schedule/", icon: "date_range" },
      { resource: "management.supervisor_work_schedule", label: "Графік супервайзерів", path: "/management/supervisor-work-schedule/", icon: "calendar_view_week" },
      { resource: "management.freed_people", label: "Звільнені люди", path: "/management/freed-people/", icon: "lock" },
    ];

    const isDefaultRoleResource = (resource) =>
      rolePrefix && resource?.startsWith(`${rolePrefix}.`);

    const allowItem = (item) => {
      if (!item?.resource) return { allowed: true, disabled: false };
      if (canAccess(rules, item.resource, "GET")) return { allowed: true, disabled: false };
      if (isDefaultRoleResource(item.resource)) return { allowed: true, disabled: true };
      return { allowed: false, disabled: false };
    };

    const sections = menuSections
      .map((section) => {
        const items = (section.items || [])
          .map((item) => {
            const access = allowItem(item);
            if (!access.allowed) return null;
            if (item.subMenu) {
              const subMenu =
                item.subMenu
                  ?.map((subItem) => {
                    const subAccess = allowItem(subItem);
                    if (!subAccess.allowed) return null;
                    return {
                      ...subItem,
                      disabled: access.disabled || subAccess.disabled,
                    };
                  })
                  .filter(Boolean) || [];
              if (!subMenu.length) return null;
              return { ...item, subMenu, disabled: access.disabled };
            }
            return { ...item, disabled: access.disabled };
          })
          .filter(Boolean);
        return items.length ? { ...section, items } : null;
      })
      .filter(Boolean);

    const existingResources = new Set();
    const existingPaths = new Set();
    sections.forEach((section) => {
      (section.items || []).forEach((item) => {
        if (item?.resource) existingResources.add(item.resource);
        if (item?.path) existingPaths.add(item.path);
      });
    });

    const extraItems = RESOURCE_ITEMS
      .map((item) => {
        if (existingResources.has(item.resource) || existingPaths.has(item.path)) {
          return null;
        }
        const access = allowItem(item);
        if (!access.allowed) return null;
        return { ...item, disabled: access.disabled };
      })
      .filter(Boolean);

    if (extraItems.length > 0) {
      if (sections.length > 0) {
        sections[0] = {
          ...sections[0],
          items: [...sections[0].items, ...extraItems],
        };
      } else {
        sections.push({ title: "Сторінки", items: extraItems });
      }
    }

    const labelCounts = sections.reduce((acc, section) => {
      (section.items || []).forEach((item) => {
        if (!item?.label) return;
        acc[item.label] = (acc[item.label] || 0) + 1;
      });
      return acc;
    }, {});

    return sections.map((section) => ({
      ...section,
      items: section.items.map((item) => {
        const badge = labelCounts[item.label] > 1 ? getRoleBadge(item.resource) : null;
        return { ...item, badge };
      }),
    }));
  }, [menuSections, rules, rolePrefix]);

  return (
    <Box className="leftSidebarDark hide-for-horizontal-nav">
      <Box className="left-sidebar-menu">
        <Box className="logo">
          <Link to="/">
            {logo || (
              <>
                <img
                  src="/img/logo.svg"
                  alt="logo-icon"
                  width={92}
                  height={35}
                />
              </>
            )}
          </Link>
        </Box>

        <Box className="burger-menu" onClick={toggleActive}>
          <Typography component={"span"} className="top-bar" />
          <Typography component={"span"} className="middle-bar" />
          <Typography component={"span"} className="bottom-bar" />
        </Box>

        <Box className="sidebar-inner">
          <Box className="sidebar-menu">
            {filteredSections.map((section, sectionIndex) => (
              <React.Fragment key={sectionIndex}>
                {section.title && (
                  <Typography
                    className="sub-title"
                    sx={{
                      display: "block",
                      fontWeight: "500",
                      textTransform: "uppercase",
                    }}
                  >
                    {section.title}
                  </Typography>
                )}

                {section.items.map((item, itemIndex) =>
                  item.subMenu ? (
                    <Accordion
                      key={itemIndex}
                      expanded={expanded === item.panelId}
                      onChange={handleChange(item.panelId)}
                      className="mat-accordion"
                    >
                    <AccordionSummary
                      expandIcon={
                        <ArrowForwardIosSharpIcon
                          sx={{ fontSize: "0.9rem" }}
                        />
                      }
                      aria-controls={`${item.panelId}-content`}
                      id={`${item.panelId}-header`}
                      className={item.disabled ? "menu-item-disabled" : ""}
                      sx={{
                        alignItems: "center",
                        "& .MuiAccordionSummary-expandIconWrapper": {
                          marginLeft: "auto", // pushes the arrow to the right
                        },
                          "& .MuiAccordionSummary-content": {
                            marginRight: 1, // small spacing between text and arrow
                          },
                        }}
                      >
                        {item.icon && (
                          <i
                            className="material-symbols-outlined notranslate"
                            translate="no"
                            style={{ marginRight: 8 }}
                          >
                            {item.icon}
                          </i>
                        )}
                        <Typography component="span" className="title">
                          {item.label}
                          {item.badge && (
                            <span
                              className={`menu-role-badge menu-role-badge--${item.badge.toLowerCase()}`}
                            >
                              {item.badge}
                            </span>
                          )}
                        </Typography>
                      </AccordionSummary>

                      <AccordionDetails className="mat-details">
                        <ul className="sidebar-sub-menu">
                          {item.subMenu.map((subItem, subIndex) => (
                            <li className="sidemenu-item" key={subIndex}>
                              {subItem.disabled ? (
                                <span className="sidemenu-link disabled">
                                  {subItem.label}
                                </span>
                              ) : (
                                <Link
                                  to={subItem.path}
                                  className={`sidemenu-link ${
                                    isActiveLink(subItem.path) ? "active" : ""
                                  }`}
                                >
                                  {subItem.label}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      </AccordionDetails>
                    </Accordion>
                  ) : (
                    <React.Fragment key={itemIndex}>
                      {item.disabled ? (
                        <span className="sidebar-menu-link disabled">
                          {item.icon && (
                            <i
                              className="material-symbols-outlined notranslate"
                              translate="no"
                            >
                              {item.icon}
                            </i>
                          )}
                          <Typography component={"span"} className="title">
                            {item.label}
                            {item.badge && (
                              <span
                                className={`menu-role-badge menu-role-badge--${item.badge.toLowerCase()}`}
                              >
                                {item.badge}
                              </span>
                            )}
                          </Typography>
                        </span>
                      ) : (
                        <Link
                          to={item.path}
                          className={`sidebar-menu-link ${
                            isActiveLink(item.path) ? "active" : ""
                          }`}
                        >
                          {item.icon && (
                            <i
                              className="material-symbols-outlined notranslate"
                              translate="no"
                            >
                              {item.icon}
                            </i>
                          )}
                          <Typography component={"span"} className="title">
                            {item.label}
                            {item.badge && (
                              <span
                                className={`menu-role-badge menu-role-badge--${item.badge.toLowerCase()}`}
                              >
                                {item.badge}
                              </span>
                            )}
                          </Typography>
                        </Link>
                      )}
                    </React.Fragment>
                  )
                )}
              </React.Fragment>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default LeftSidebarMenu;
